import { AnimalApiId } from '@/model/Animal.model.ts';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';
import {
  SampleDefinitionApiId,
  SampleDefinitionFormData,
  SampleDefinitionV1,
  SamplingSheetBaseColumn,
  SamplingSheetColumnId,
} from '@/model/SamplePlan.model';

import { StudyApiId } from '@/model/Study.model.ts';
import { _isNil, _notNil } from '@/littledash.ts';
import { AnimalForSampling } from './AnimalSelectionTable.utils.ts';
import { RouteService } from '@/support/RouteService.ts';

export interface ColumnOption {
  id: SamplingSheetColumnId;
  label: string;
  required?: boolean;
  defaultSelected?: boolean;
  order?: number;
}

/**
 * Comparator function for sorting column options in the sampling sheet column selector.
 *
 * Sort order:
 * 1. Required columns come first (regardless of order property)
 * 2. Within required/non-required groups: columns with `order` property come first, sorted by order value (ascending)
 * 3. Within required/non-required groups: columns without `order` property come after, sorted alphabetically by label
 * 4. Alphabetical sorting is numeric-aware (e.g., "Item 2" before "Item 10")
 */
export const columnComparator = (a: ColumnOption, b: ColumnOption): number => {
  // Required columns always come first
  if (a.required === true && b.required !== true) {
    return -1;
  }
  if (b.required === true && a.required !== true) {
    return 1;
  }

  // Within same required/non-required group, sort by order then alphabetically
  if (_notNil(a.order) && _notNil(b.order)) {
    return a.order - b.order;
  }
  if (_notNil(a.order)) {
    return -1;
  }
  if (_notNil(b.order)) {
    return 1;
  }
  return a.label.localeCompare(b.label, undefined, { numeric: true });
};

export const BaseColumns: Record<SamplingSheetBaseColumn, ColumnOption> = {
  collected_at: { id: 'collected_at', label: 'Collected at (time)', required: true, defaultSelected: true, order: 1 },
  sample_id: { id: 'sample_id', label: 'Sample ID', defaultSelected: true, order: 2 },
  sample_status: { id: 'sample_status', label: 'Sample status', order: 3 },
  sample_details: { id: 'sample_details', label: 'Sample details (e.g. weight, volume, etc.)', order: 4 },
  planned_time: { id: 'planned_time', label: 'Planned time', order: 5 },
  sample_comments: { id: 'sample_comments', label: 'Sample Comments', order: 6 },
} as const;

export const defaultSelectedColumns = Object.entries(BaseColumns).reduce<Set<SamplingSheetColumnId>>(
  (acc, [id, col]) => {
    if (col.defaultSelected === true) {
      acc.add(id as SamplingSheetColumnId);
    }
    return acc;
  },
  new Set()
);

/**
 * Get the set of required columns based on whether calculate_collection_time is enabled.
 * - Starts with base columns that are always required (have required: true)
 * - Adds planned_time when calculate_collection_time is true
 *
 * @param hasCalculateCollectionTime - Whether any sample definition has calculate_collection_time enabled
 * @returns Set of column IDs that must be included in the sampling sheet
 */
export const getRequiredColumns = (hasCalculateCollectionTime: boolean): Set<SamplingSheetColumnId> => {
  // Get all base columns that are marked as always required
  const required = Object.entries(BaseColumns).reduce<Set<SamplingSheetColumnId>>((acc, [id, col]) => {
    if (col.required === true) {
      acc.add(id as SamplingSheetColumnId);
    }
    return acc;
  }, new Set());

  // Add conditionally required columns
  if (hasCalculateCollectionTime) {
    required.add(BaseColumns.planned_time.id);
  }

  return required;
};

export const buildRegisteredAnimalsMap = (registeredAnimalsResponse: any): Record<AnimalApiId, AnimalForSampling> => {
  const registeredAnimalsWithSampleGroups = registeredAnimalsResponse?.body?.data ?? [];
  return registeredAnimalsWithSampleGroups.reduce(
    (map: Record<AnimalApiId, AnimalForSampling>, animal: AnimalForSampling) => {
      map[animal.api_id] = animal;
      return map;
    },
    {} as Record<AnimalApiId, AnimalForSampling>
  );
};

export const isAnimalEligibleForSampleDefinition = (
  animalId: AnimalApiId,
  sampleDef: SampleDefinitionFormData,
  registeredAnimalsMap: Record<AnimalApiId, AnimalForSampling>,
  animalIdDefinitionMap: Map<AnimalApiId, AnimalDefinitionApiId>
): boolean => {
  const animal = registeredAnimalsMap?.[animalId];
  if (_isNil(animal)) {
    return false;
  }

  // First check if the animal has this sample definition in their sample_groups
  const animalSampleGroupIds = animal.sample_groups?.map((sg: SampleDefinitionV1) => sg.api_id) ?? [];
  if (!animalSampleGroupIds.includes(sampleDef.api_id as SampleDefinitionApiId)) {
    return false;
  }

  // Check if animal is eligible for this sample definition based on targets
  if (sampleDef.target.type === 'registered_animals') {
    return sampleDef.target.targets.includes(animalId as AnimalApiId);
  }

  // For group targets, check if animal's group matches
  if (sampleDef.target.type === 'groups' && _notNil(animal.study_group)) {
    return sampleDef.target.targets.includes(animal.study_group.api_id);
  }

  if (sampleDef.target.type === 'planned_animals') {
    // For planned animals, check if this animal's definition ID matches the target
    const animalDefinitionId = animal.animal_definition_api_id || animalIdDefinitionMap.get(animalId);
    return _notNil(animalDefinitionId) && sampleDef.target.targets.includes(animalDefinitionId);
  }

  return false;
};

export const buildSampleDefinitionsMap = (
  selectedAnimals: Set<AnimalApiId>,
  sampleDefinitions: Array<SampleDefinitionFormData>,
  registeredAnimalsMap: Record<AnimalApiId, AnimalForSampling>,
  animalIdDefinitionMap: Map<AnimalApiId, AnimalDefinitionApiId>
): Record<SampleDefinitionApiId, Array<AnimalApiId>> => {
  return sampleDefinitions.reduce<Record<SampleDefinitionApiId, Array<AnimalApiId>>>((acc, sampleDef) => {
    const eligibleAnimals = Array.from(selectedAnimals).filter((animalId) =>
      isAnimalEligibleForSampleDefinition(animalId, sampleDef, registeredAnimalsMap, animalIdDefinitionMap)
    );
    if (eligibleAnimals.length > 0) {
      acc[sampleDef.api_id as SampleDefinitionApiId] = eligibleAnimals;
    }
    return acc;
  }, {});
};

export const generateStudySamplesRoute = (studyId: string) => {
  return RouteService.web({
    route: 'studies.samples',
    path: { id: studyId },
  }).route;
};

export const isLoadingRequiredData = (
  groupsLoading: boolean,
  samplePlanLoading: boolean,
  registeredAnimalsLoading: boolean,
  tableData: any
): boolean => {
  return groupsLoading || samplePlanLoading || registeredAnimalsLoading || !tableData;
};

export const hasValidExecutionState = (
  studyApiId: StudyApiId | undefined,
  sampleDefinitionIds: Array<SampleDefinitionApiId>,
  selectedAnimals: Set<AnimalApiId>
): boolean => {
  return _notNil(studyApiId) && sampleDefinitionIds.length > 0 && selectedAnimals.size > 0;
};

export const calculateTotalSamplesFromMap = (
  sampleDefinitions: Array<SampleDefinitionFormData>,
  sampleDefinitionsMap: Record<SampleDefinitionApiId, Array<AnimalApiId>>
): number => {
  return Object.entries(sampleDefinitionsMap).reduce((total, [sampleDefId, eligibleAnimals]) => {
    const sampleDef = sampleDefinitions.find((def) => def.api_id === sampleDefId);
    if (_isNil(sampleDef)) {
      return total;
    }

    const timepointsCount = sampleDef.timepoints?.length ?? 0;
    const samplesPerAnimal = Math.max(timepointsCount, 1);

    return total + eligibleAnimals.length * samplesPerAnimal;
  }, 0);
};

export const calculateTotalSamples = (
  selectedAnimals: Set<AnimalApiId>,
  sampleDefinitions: Array<SampleDefinitionFormData>,
  registeredAnimalsResponse: any,
  animalIdDefinitionMap: Map<AnimalApiId, AnimalDefinitionApiId>
): number => {
  if (selectedAnimals.size === 0 || registeredAnimalsResponse?.type !== 'success') {
    return 0;
  }

  const registeredAnimalsMap = buildRegisteredAnimalsMap(registeredAnimalsResponse);
  const sampleDefinitionsMap = buildSampleDefinitionsMap(
    selectedAnimals,
    sampleDefinitions,
    registeredAnimalsMap,
    animalIdDefinitionMap
  );

  return calculateTotalSamplesFromMap(sampleDefinitions, sampleDefinitionsMap);
};
